import * as React from "react";
import { FilterFilled, FilterOutlined } from "@ant-design/icons";
import { Dropdown, Checkbox, Radio, DropdownProps } from "antd";
import { FilterConfig } from "../ScrollableTable";
import isEqual from "lodash/isEqual";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { SearchBar } from "Components/General/SearchBar";
import { debounce } from "lodash";
import { getFilterPopupContainer } from "./filterPopupContainer";
import { filterControlTestId } from "./filterControlTestId";

interface FiltersDropdownProps extends WithTranslation {
  columnDataKey: string;
  filters: FilterConfig[];
  selectedFilters: string[] | number[];
  onFilter: (filters: string[] | number[]) => void;
  singleFilter?: boolean;
  dropdownMinWidth?: number;
  filterIconDataTestId?: string;
}

interface FiltersDropdownState {
  visible: boolean;
  selectedKeys: string[] | number[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function stopPropagation(e: React.SyntheticEvent<any>) {
  e.stopPropagation();
  if (e.nativeEvent.stopImmediatePropagation) {
    e.nativeEvent.stopImmediatePropagation();
  }
}

export const FiltersDropdown = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<FiltersDropdownProps, FiltersDropdownState> {
      constructor(props: FiltersDropdownProps) {
        super(props);
        this.state = {
          visible: false,
          selectedKeys: [],
        };

        this.debounceSearch = debounce(this.handleSearch, 200);
      }
      private debounceSearch: (query: string) => void;

      componentDidMount() {
        if (this.props.selectedFilters) {
          this.setState({ selectedKeys: this.props.selectedFilters });
        }
      }

      componentDidUpdate(prevProps: FiltersDropdownProps) {
        if (JSON.stringify(prevProps.selectedFilters) !== JSON.stringify(this.props.selectedFilters)) {
          this.setState({ selectedKeys: this.props.selectedFilters || [] });
        }
      }

      setSelectedKeys = ({ key }) => {
        if (this.props.singleFilter) {
          this.setState({ selectedKeys: [key] });
        } else {
          const isKeyPresent = (this.state.selectedKeys as string[]).includes(key);
          let selectedKeys;
          if (isKeyPresent) {
            selectedKeys = (this.state.selectedKeys as string[]).filter((item) => item !== key);
          } else {
            selectedKeys = (this.state.selectedKeys as string[]).concat(key);
          }
          this.setState({ selectedKeys });
        }
      };

      setVisible = (visible: boolean) => {
        this.setState({ visible });
      };

      handleClearFilters = () => {
        this.setState(
          {
            selectedKeys: [],
          },
          this.handleConfirm
        );
      };

      handleSelectAll = () => {
        if (this.state.selectedKeys && this.state.selectedKeys.length > 0) {
          this.setState({ selectedKeys: [] });
        } else {
          const { filters } = this.props;
          this.setState({ selectedKeys: filters.map((a) => a.value.toString()) });
        }
      };

      handleSearch = (val: string) => {
        const filters = this.props.filters.slice();
        const selectedKeys = [];
        if (!val) {
          this.setState({ selectedKeys: [] });
          return;
        }
        const query = val.toLocaleLowerCase();
        for (const filter of filters) {
          // find filter value that will be compared with search query. If text is a Node, then try for value.
          // Any of these should be string
          const valueToCompare =
            typeof filter.text === "string" ? filter.text : typeof filter.value === "string" ? filter.value : "";
          if (
            valueToCompare.toLocaleLowerCase().indexOf(query) !== -1 &&
            (!filter.description || filter.description.toLocaleLowerCase().indexOf(query) !== -1)
          ) {
            selectedKeys.push(filter.value);
          }
        }
        this.setState({ selectedKeys: selectedKeys });
      };

      handleConfirm = () => {
        this.setVisible(false);
        this.confirmFilter();
      };

      onVisibleChange: DropdownProps["onOpenChange"] = (open) => {
        this.setVisible(open);
        if (!open) {
          this.confirmFilter();
        }
      };

      confirmFilter() {
        const { selectedKeys } = this.state;
        if (!isEqual(selectedKeys, this.props.selectedFilters)) {
          this.props.onFilter(selectedKeys);
        }
      }

      private readonly tid = (base: string) => filterControlTestId(base, this.props.columnDataKey);

      render() {
        const { filters, selectedFilters, t, singleFilter, filterIconDataTestId } = this.props;
        const { visible, selectedKeys } = this.state;
        const hasNoFilters = !selectedFilters || !selectedFilters.length;

        return (
          <div onClick={stopPropagation}>
            <Dropdown
              trigger={["click"]}
              placement="bottomRight"
              getPopupContainer={(node) => getFilterPopupContainer(node as HTMLElement)}
              menu={{
                selectable: true,
                multiple: true,
                onClick: this.setSelectedKeys,
                selectedKeys: selectedKeys as string[],
                style: { maxHeight: 300, overflow: "auto", minWidth: this.props.dropdownMinWidth },
                items: filters.map((item) => ({
                  label: (
                    <span data-testid={this.tid(`scrollable-filter-dropdown-option-${item.value}`)}>
                      {singleFilter ? (
                        <Radio checked={selectedKeys && (selectedKeys as string[]).includes(item.value.toString())} />
                      ) : (
                        <Checkbox
                          checked={selectedKeys && (selectedKeys as string[]).includes(item.value.toString())}
                        />
                      )}{" "}
                      {item.text}
                      {item.description && <span style={{ fontSize: 12 }}>({item.description})</span>}
                    </span>
                  ),
                  key: item.value,
                  hidden: item.hidden,
                  className: "filter-menu-item",
                })),
              }}
              popupRender={(menus) => (
                <div className="filter-dropdown" data-testid={this.tid("scrollable-filter-dropdown")}>
                  {menus}
                  <div className={`filter-dropdown-btns`}>
                    <SearchBar
                      searchOnChange={true}
                      onPressEnter={this.handleConfirm}
                      placeholder={t("Search")}
                      onSearch={this.debounceSearch}
                      inputDataTestId={this.tid("scrollable-filter-dropdown-search")}
                      style={{ marginBottom: "10px", marginTop: "5px" }}
                    />

                    <a
                      className="filter-dropdown-link"
                      onClick={this.handleConfirm}
                      onKeyDown={(e) => e.key === "Enter" && this.handleConfirm()}
                      role="button"
                      tabIndex={0}
                      aria-label={t("Ok")}
                      data-testid={this.tid("scrollable-filter-dropdown-ok")}
                    >
                      {t("Ok")}
                    </a>
                    <a
                      className="filter-dropdown-link clear"
                      onClick={this.handleClearFilters}
                      onKeyDown={(e) => e.key === "Enter" && this.handleClearFilters()}
                      role="button"
                      tabIndex={0}
                      aria-label={t("Reset")}
                      data-testid={this.tid("scrollable-filter-dropdown-reset")}
                    >
                      {t("Reset")}
                    </a>
                    {!singleFilter ? (
                      <a
                        className="filter-dropdown-link clear"
                        onClick={this.handleSelectAll}
                        onKeyDown={(e) => e.key === "Enter" && this.handleSelectAll()}
                        role="button"
                        tabIndex={0}
                        aria-label={t("choose all")}
                        data-testid={this.tid("scrollable-filter-dropdown-choose-all")}
                      >
                        {t("choose all")}
                      </a>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
              )}
              open={visible}
              forceRender={true}
              onOpenChange={this.onVisibleChange}
            >
              <div className="filter-icon" data-testid={filterIconDataTestId}>
                {hasNoFilters ? <FilterOutlined /> : <FilterFilled />}
              </div>
            </Dropdown>
          </div>
        );
      }
    }
  )
);
